import { useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Slider as SliderElement } from '@elfsight/lib-toolkit-slider';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { isEqual } from 'es-toolkit';
import { meta } from '../../../../meta';
import { getImagesByQuality, getQuality } from '../../../helpers';
import { useResizeWidth } from '../../../utils';
import { calcSliderIndexesOfImagesToLoad } from '../../lazy-loading';
import { Arrows } from './arrows/Arrows';
import { SliderItem } from './SliderItem';

export const Slider = ({ images, onClick, reportVisibleImages }) => {
  const {
    sliderArrowsEnabled,
    sliderSwipeEnabled,
    sliderTransitionDuration,
    sliderPaginationType,
    sliderAutoSlideEnabled,
    sliderAutoSlideDelay,
    sliderAutoSlidePauseOnHover,
    sliderElementsColor,
    sliderImageRatio,
    sliderLoop,
    sliderAnimationType,
    displayDeactivation
  } = useSettings();
  const { measureRef, width } = useResizeWidth();
  const quality = getQuality(width);
  const currentImages = getImagesByQuality(images, quality);

  const [oldImages, setOldImages] = useState([]);
  const [{ id: currentItemId }, setActiveSlide] = useState({
    id: 0,
    item: currentImages[0]
  });
  const [isDrag, toggleDrag] = useState(false);
  const slider = useRef(null);

  const onTranslateStart = () => {
    const {
      current: {
        state: { horizontalMove }
      }
    } = slider;

    if (horizontalMove) toggleDrag(true);
  };

  const onTranslateEnd = () => {
    const {
      current: {
        state: { realIndex }
      }
    } = slider;

    setActiveSlide({
      id: realIndex,
      item: images[realIndex]
    });

    const indexesOfImagesToLoad = calcSliderIndexesOfImagesToLoad({
      currentIndex: realIndex,
      visibleSlidesCount: 1,
      maxIndex: images.length - 1
    });

    reportVisibleImages(indexesOfImagesToLoad.map((index) => images[index].id));

    requestAnimationFrame(() => {
      toggleDrag(false);
    });
  };

  useLayoutEffect(() => {
    const { current: sliderElement } = slider;
    const notEquals = isEqual(images, oldImages);

    if (notEquals && notEquals.id !== currentItemId && sliderElement) {
      sliderElement.goTo(notEquals.id);
    } else {
      sliderElement.goTo(0);
    }

    setOldImages(images);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [images, slider, sliderImageRatio]);

  const autoplayDelay =
    sliderAutoSlideEnabled && !displayDeactivation && sliderAutoSlideDelay > 0
      ? sliderAutoSlideDelay
      : 0;

  return (
    <Container ref={measureRef} $paginationType={sliderPaginationType}>
      <SliderElement
        ref={slider}
        slidesPerView={1}
        colors={{
          arrows: {
            color: getBrightInvertedColor(sliderElementsColor, [
              'rgb(255, 255, 255)',
              'rgb(29, 33, 41)'
            ]),
            background: changeColorAlpha(sliderElementsColor, -0.7),
            hover: {
              color: getBrightInvertedColor(sliderElementsColor, [
                'rgb(255, 255, 255)',
                'rgb(29, 33, 41)'
              ]),
              background: changeColorAlpha(sliderElementsColor, -0.7)
            }
          },
          pagination: {
            color: changeColorAlpha(sliderElementsColor, -0.7),
            active: sliderElementsColor
          }
        }}
        speed={sliderTransitionDuration}
        paginationPosition="outside"
        paginationAlign="center"
        paginationClassName={`${meta.stylePrefix}-slider-pagination`}
        itemClassName={`${meta.stylePrefix}-slider-item`}
        navigationClassName={`${meta.stylePrefix}-slider-navigation`}
        appClassnamePrefix={meta.stylePrefix}
        autoplayDelay={autoplayDelay}
        arrows={sliderArrowsEnabled}
        drag={sliderSwipeEnabled}
        autoplayPauseOnHover={sliderAutoSlidePauseOnHover}
        paginationType={sliderPaginationType}
        onTranslateEnd={onTranslateEnd}
        onTranslateStart={onTranslateStart}
        customIcons={{
          prev: <Arrows currentItemId={currentItemId} rotate />,
          next: <Arrows currentItemId={currentItemId} />
        }}
        loop={sliderLoop}
        animationType={sliderAnimationType}
      >
        {currentImages.map((item, index) => (
          <SliderItem
            key={item.id}
            id={index}
            data={item}
            isDrag={isDrag}
            onClick={onClick}
          />
        ))}
      </SliderElement>
    </Container>
  );
};

const Container = styled.div.attrs({
  className: `${meta.stylePrefix}-slider-component`
})`
  display: block;
  max-width: 960px;
  margin: 0 auto;

  & .${meta.stylePrefix}-slider-slider {
    ${({ $paginationType }) =>
      $paginationType === 'bullets' &&
      css`
        padding: 0 0 32px !important;
        height: max-content !important;
      `}
  }

  & .${meta.stylePrefix}-slider-navigation-arrow {
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;

    ${({ $paginationType }) =>
      $paginationType === 'bullets'
        ? css`
            top: calc(50% - 32px);
          `
        : css`
            top: 50%;
            transform: translateY(-50%);
          `}
  }

  & .${meta.stylePrefix}-arrow-component {
    box-shadow: none;
  }

  & .${meta.stylePrefix}-navigation-previous {
    left: 20px;
  }

  & .${meta.stylePrefix}-navigation-next {
    right: 20px;
  }

  & .${meta.stylePrefix}-arrow-icon {
    width: 24px;
    height: 24px;
  }

  & .${meta.stylePrefix}-slider-item {
    display: flex;
  }

  & .${meta.stylePrefix}-slider-pagination {
    display: flex;
  }
`;
